<template>
  <div class="bg p-3 pb-24 mb-auto h-full overflow-auto scrollbar-custom" id="style-1">
    <div class="max-w-full mx-auto">
      <!-- Header -->
      <div class="flex justify-between items-center mb-4">
        <div class="flex items-center gap-3">
          <i class="fas fa-shop text-indigo-300 text-xl"></i>
          <h1 class="text-2xl font-bold text-indigo-300">ตลาด</h1>
          <span class="text-sm text-gray-400">({{ activeShopsCount }} ร้านค้าเปิดอยู่)</span>
        </div>
        <div>
          <span
            class="chip"
            :class="hasActiveShop ? 'chip-green' : 'chip-muted'"
          >
            <i class="fas" :class="hasActiveShop ? 'fa-check-circle' : 'fa-minus-circle'"></i>
            {{ hasActiveShop ? 'มีร้านค้าของคุณกำลังเปิดอยู่' : 'ยังไม่มีร้านค้าของคุณ' }}
          </span>
        </div>
      </div>

      <!-- Toolbar -->
      <section class="bg-gray-900 bg-opacity-80 rounded-md p-3 mb-4 border border-gray-700">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <div class="flex items-center gap-2 flex-wrap">
            <button
              class="bg-indigo-600 hover:bg-indigo-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
              @click="openSetShopModal"
              :disabled="isActionLoading || hasActiveShop"
              title="ตั้งร้านค้า"
            >
              <span v-if="!isActionLoading"><i class="fas fa-shop mr-2 text-xs"></i> ตั้งร้านค้า</span>
              <span v-else class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            </button>
            <!-- Search Input -->
            <input
              v-model.trim="searchQuery"
              type="text"
              class="bg-gray-900 text-gray-200 border border-gray-700 rounded-md px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="ค้นหาไอเทมหรือประเภท..."
            />
          </div>
          <!-- Sort Buttons -->
          <div class="flex items-center gap-1 border border-gray-700 rounded-lg overflow-hidden">
            <button
              class="px-3 py-2 text-sm transition-colors duration-200"
              :class="sortMode === 'oldest' ? 'bg-gray-800 text-indigo-200' : 'bg-transparent text-gray-400 hover:text-indigo-300'"
              @click="sortMode = 'oldest'"
              title="เรียงจากเก่าสุด"
            >
              <i class="fas fa-sort-amount-down-alt mr-1"></i> เก่าสุด
            </button>
            <button
              class="px-3 py-2 text-sm transition-colors duration-200"
              :class="sortMode === 'newest' ? 'bg-gray-800 text-indigo-200' : 'bg-transparent text-gray-400 hover:text-indigo-300'"
              @click="sortMode = 'newest'"
              title="เรียงจากล่าสุด"
            >
              <i class="fas fa-sort-amount-up-alt mr-1"></i> ล่าสุด
            </button>
          </div>
        </div>
      </section>

      <!-- Messages -->
      <div v-if="warningMessage" class="rounded-md border border-yellow-700 bg-yellow-900 bg-opacity-10 text-yellow-200 p-3 mb-3 text-sm">
        <i class="fas fa-exclamation-triangle mr-1"></i> {{ warningMessage }}
      </div>
      <div v-if="error" class="rounded-md border border-red-700 bg-red-900 bg-opacity-10 text-red-200 p-3 mb-3 text-sm">
        <i class="fas fa-times-circle mr-1"></i> {{ error }}
      </div>

      <!-- Loading Skeleton -->
      <div v-if="isFetching" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div v-for="n in 6" :key="'s'+n" class="h-36 rounded-md border border-gray-700 bg-gradient-to-r from-gray-700 via-gray-600 to-gray-700 bg-opacity-30 animate-pulse"></div>
      </div>

      <!-- Shops Grid -->
      <div v-else>
        <div v-if="displayedShops.length" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <article
            v-for="shop in displayedShops"
            :key="shop.id"
            class="border border-gray-700 rounded-md bg-gray-900 bg-opacity-80 p-3 cursor-pointer transition-all duration-200 hover:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 relative"
            :class="{ 'bg-indigo-900 bg-opacity-20': shop.characterId === user?.value?.uid }"
            @click="openShopItemsModal(shop)"
            tabindex="0"
            @keydown.enter.prevent="openShopItemsModal(shop)"
            @keydown.space.prevent="openShopItemsModal(shop)"
            :aria-label="`เปิดร้านค้า ${shop.shopName || 'ร้านค้าไม่มีชื่อ'}`"
          >
            <!-- Ownership Indicator -->
            <div v-if="shop.characterId === user?.value?.uid" class="absolute top-2 right-2 text-indigo-300">
              <i class="fas fa-crown text-sm" title="ร้านค้าของฉัน"></i>
            </div>
            <!-- Head -->
            <div class="flex items-center gap-3">
              <img
                :src="shop.hideIdentity ? anonymouseImg : shop.characterImg"
                alt=""
                class="w-12 h-12 rounded-full object-cover border-2 border-indigo-500 bg-opacity-50"
                @error="handleImageError"
              />
              <div class="min-w-0">
                <h3 class="text-base font-semibold text-indigo-200 truncate">{{ shop.shopName || 'ร้านค้าไม่มีชื่อ' }}</h3>
                <div class="flex items-center gap-2 text-xs text-gray-400">
                  <span class="chip chip-muted">
                    <i class="fas" :class="shop.hideIdentity ? 'fa-user-secret' : 'fa-user'"></i>
                    {{ shop.hideIdentity ? 'บุคคลลึกลับ' : shop.characterName || 'ไม่ทราบ' }}
                  </span>
                  <span class="w-1 h-1 rounded-full bg-gray-500"></span>
                  <span class="text-gray-400"><i class="fas fa-box mr-1"></i>{{ shop.items?.length || 0 }} ชิ้น</span>
                </div>
              </div>
            </div>
            <!-- Body -->
            <div class="mt-3">
              <div class="flex flex-wrap gap-2">
                <span class="chip">
                  <i class="fas fa-gem text-xs text-blue-500"></i>
                  เริ่มต้น {{ shop.__minPrice != null ? shop.__minPrice : '-' }} เพชร
                </span>
                <span class="chip chip-amber">
                  <i class="far fa-clock text-xs"></i>
                  ปิดร้าน {{ formatExpiresAt(shop.expiresAt) }}
                </span>
              </div>
              <div class="mt-3 flex items-center gap-2 min-h-[44px]">
                <template v-if="shop.items && shop.items.length">
                  <img
                    v-for="(it, idx) in shop.items.slice(0,4)"
                    :key="it.id + idx"
                    :src="it.img || defaultItemImg"
                    alt=""
                    class="w-10 h-10 rounded-md object-cover border border-gray-700 bg-gray-800"
                    :class="[it.rarityColors?.borderColor || 'border-gray-700']"
                    @error="handleImageError"
                  />
                  <div v-if="shop.items.length > 4" class="text-xs text-gray-400">+{{ shop.items.length - 4 }}</div>
                </template>
                <div v-else class="text-xs text-gray-400 border border-dashed border-gray-700 rounded-md px-2 py-1">ไม่มีไอเทม</div>
              </div>
            </div>
          </article>
        </div>
        <!-- Empty State -->
        <div v-else class="border border-dashed border-gray-700 rounded-md p-6 text-center bg-gray-800 bg-opacity-60">
          <i class="fas fa-shop text-3xl text-indigo-300 mb-2"></i>
          <h2 class="text-lg font-semibold text-indigo-200">ยังไม่มีร้านค้าที่เปิดอยู่</h2>
          <p class="text-gray-400 mb-3">เริ่มจากตั้งร้านค้าของคุณ</p>
          <button
            class="bg-indigo-600 hover:bg-indigo-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
            @click="openSetShopModal"
            :disabled="isActionLoading || hasActiveShop"
          >
            <i class="fas fa-shop mr-2 text-xs"></i> ตั้งร้านค้า
          </button>
        </div>
      </div>

      <!-- Set Shop Modal -->
      <transition name="fade">
        <div v-if="showSetShopModal" class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 animate-modalFade">
          <div class="w-full max-w-2xl bg-gray-800 text-gray-300 rounded-lg p-0 flex flex-col gap-0 relative shadow-lg border border-gray-700 animate-scaleIn">
            <!-- Head -->
            <div class="flex justify-between items-center border-b border-gray-700 px-4 py-3 bg-gray-900 bg-opacity-95 backdrop-filter backdrop-blur">
              <h3 class="text-lg font-semibold text-indigo-200"><i class="fas fa-shop mr-2"></i> ตั้งร้านค้า</h3>
              <button class="text-gray-400 hover:text-indigo-300 transition-colors duration-200" @click="closeSetShop" aria-label="ปิด">
                <i class="fas fa-times text-lg"></i>
              </button>
            </div>
            <!-- Content -->
            <div class="p-4 overflow-auto max-h-96 scrollbar-custom">
              <div class="mb-3">
                <div class="rounded-md border border-yellow-700 bg-yellow-900 bg-opacity-10 text-yellow-200 p-3 mb-3 text-sm">
                  <i class="fas fa-exclamation-triangle mr-1"></i> คุณสามารถเปิดร้านได้ทุกๆ 48 ชั่วโมง และร้านค้าจะหมดอายุภายใน 9 ชั่วโมง
                </div>
                <div
                  class="rounded-md border p-3 mb-3 text-sm"
                  :class="canOpenShop ? 'border-green-700 bg-green-900 bg-opacity-10 text-green-200' : 'border-yellow-700 bg-yellow-900 bg-opacity-10 text-yellow-200'"
                >
                  <i class="fas mr-1" :class="canOpenShop ? 'fa-check-circle' : 'fa-clock'"></i> {{ cooldownMessage }}
                </div>
                <label class="block text-sm text-gray-400 mb-1">ชื่อร้านค้า (สูงสุด 50 อักษร) <span class="text-red-300">*</span></label>
                <input
                  v-model.trim="shopName"
                  type="text"
                  maxlength="50"
                  class="w-full bg-gray-900 text-gray-200 border border-gray-700 rounded-md px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="เช่น อาวุธ 10 ดาว"
                  :disabled="isActionLoading"
                />
                <div class="text-right text-xs text-gray-400 mt-1">{{ shopName.length }} / 50 อักษร</div>
              </div>

              <!-- Identity Switch -->
              <div
                class="flex items-center gap-3 py-2 cursor-pointer select-none"
                role="switch"
                :aria-checked="hideIdentity"
                @click="hideIdentity = !hideIdentity"
              >
                <div
                  class="w-12 h-7 rounded-full border border-gray-700 relative transition-colors duration-200"
                  :class="hideIdentity ? 'bg-indigo-600 bg-opacity-40 border-indigo-600' : 'bg-gray-700 bg-opacity-70'"
                >
                  <div
                    class="w-6 h-6 rounded-full bg-white absolute top-1/2 transform -translate-y-1/2 transition-all duration-200"
                    :class="hideIdentity ? 'left-6' : 'left-1'"
                  ></div>
                </div>
                <div class="text-sm font-semibold text-gray-200 flex items-center gap-2">
                  <i class="fas" :class="hideIdentity ? 'fa-user-secret' : 'fa-user'"></i>
                  {{ hideIdentity ? 'ปิดบังตัวตน (กำลังซ่อน)' : 'ปิดบังตัวตน' }}
                </div>
              </div>
              <input v-model="hideIdentity" type="checkbox" class="sr-only" aria-hidden="true" />

              <!-- Selected Items -->
              <div class="mt-3">
                <div class="flex items-center justify-between mb-2">
                  <h4 class="text-sm font-semibold text-indigo-200"><i class="fas fa-list-ul mr-2"></i> ไอเทมที่เลือก</h4>
                  <button
                    class="text-sm px-3 py-1.5 rounded-md border border-gray-700 bg-gray-900 hover:border-indigo-600 hover:text-indigo-200 transition-colors duration-200"
                    @click="openItemPicker"
                    :disabled="isActionLoading"
                    title="เพิ่มไอเทม"
                  >
                    <i class="fas fa-plus mr-1 text-xs"></i> เพิ่มไอเทม ({{ selectedItems.length }}/10)
                  </button>
                </div>
                <div v-if="selectedItems.length" class="space-y-2 scrollbar-custom">
                  <div
                    v-for="(item, index) in selectedItems"
                    :key="item.id"
                    class="flex items-center justify-between gap-2 px-3 py-2 border border-gray-700 rounded-md"
                    :style="item.rarityColors ? {
                      borderColor: item.rarityColors.borderColor,
                      backgroundColor: item.rarityColors.backgroundColor,
                      color: item.rarityColors.textColor
                    } : { borderColor: '#374151', backgroundColor: '#111827' }"
                  >
                    <div class="flex items-center gap-3 min-w-0">
                      <img
                        :src="item.img || defaultItemImg"
                        alt=""
                        class="w-10 h-10 rounded-md object-cover border border-gray-700 bg-gray-800"
                        :class="item.rarityColors?.borderColor || 'border-gray-700'"
                        @error="handleImageError"
                      />
                      <div class="min-w-0">
                        <div class="text-sm font-semibold truncate" :class="item.rarityColors?.textColor || 'text-gray-200'">
                          {{ item.name }} {{ item.level ? `+${item.level}` : '' }}
                        </div>
                        <div class="text-2xs truncate">{{ item.type }}</div>
                        <div v-if="item.rarity" class="text-2xs flex items-center gap-1">
                          {{ item.rarity }}
                          <i class="fas fa-star text-yellow-500"></i>
                        </div>
                      </div>
                    </div>
                    <div class="flex items-center gap-2">
                      <div class="flex flex-col gap-1">
                        <div class="flex items-center gap-2 bg-gray-900 border border-gray-700 rounded-md px-2 py-1">
                          <i class="fas fa-gem text-xs text-indigo-200"></i>
                          <input
                            v-model.number="item.price"
                            type="number"
                            min="10"
                            max="100000"
                            step="1"
                            oninput="this.value = Math.floor(this.value)"
                            class="w-36 bg-transparent text-sm outline-none"
                            :style="item.rarityColors ? { color: item.rarityColors.textColor } : { color: '#e5e7eb' }"
                            placeholder="ราคา (เพชร, 10-100,000, จำนวนเต็ม)"
                            :disabled="isActionLoading"
                          />
                        </div>
                        <div class="text-2xs text-gray-400">
                          <i class="fas fa-info-circle mr-1"></i>
                          คุณจะได้รับ {{ calculateNetPrice(item.price) }} เพชร (หักภาษี 35%)
                        </div>
                      </div>
                      <button
                        class="text-red-300 border border-red-700 rounded-md w-9 h-9 flex items-center justify-center hover:bg-red-900 bg-opacity-20"
                        @click="removeItem(index)"
                        :disabled="isActionLoading"
                        aria-label="ลบ"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div v-else class="text-xs text-gray-400 border border-dashed border-gray-700 rounded-md px-2 py-2">ยังไม่มีไอเทมที่เลือก</div>
              </div>

              <div v-if="modalError" :class="[modalError === 'ซื้อไอเทมสำเร็จ' ? 'border-green-700 bg-green-900 text-green-200' : 'border-red-700 bg-red-900 text-red-200']" class="mt-3 rounded-md border bg-opacity-10 p-3 text-sm">
                <i class="fas fa-times-circle mr-1"></i> {{ modalError }}
              </div>
            </div>

            <!-- Actions -->
            <div class="flex gap-2 border-t border-gray-700 px-4 py-3 sticky bottom-0 bg-gray-900 bg-opacity-95 backdrop-filter backdrop-blur">
              <button
                class="bg-indigo-600 hover:bg-indigo-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
                @click="confirmSetShop"
                :disabled="isActionLoading || selectedItems.length === 0 || !allItemsPriced || !shopName || !canOpenShop"
              >
                <span v-if="!isActionLoading"><i class="fas fa-check mr-2 text-xs"></i> ตั้งร้านค้า</span>
                <span v-else class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              </button>
              <button
                class="bg-gray-700 hover:bg-gray-600 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-gray-600"
                @click="closeSetShop"
                :disabled="isActionLoading"
              >
                <i class="fas fa-times mr-2 text-xs"></i> ยกเลิก
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Item Picker Modal -->
      <transition name="fade">
        <div v-if="showItemSelectionModal" class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 animate-modalFade">
          <div class="w-full max-w-2xl bg-gray-800 text-gray-300 rounded-lg p-0 flex flex-col relative shadow-lg border border-gray-700 animate-scaleIn">
            <!-- Head -->
            <div class="flex justify-between items-center border-b border-gray-700 px-4 py-3 sticky top-0 bg-gray-900 bg-opacity-95 backdrop-filter backdrop-blur">
              <h3 class="text-lg font-semibold text-indigo-200"><i class="fas fa-suitcase mr-2"></i> เลือกไอเทมจากกระเป๋า</h3>
              <button class="text-gray-400 hover:text-indigo-300 transition-colors duration-200" @click="showItemSelectionModal = false" :disabled="isActionLoading" aria-label="ปิด">
                <i class="fas fa-times text-lg"></i>
              </button>
            </div>
            <!-- Content -->
            <div class="p-4 overflow-auto max-h-96 scrollbar-custom">
              <div class="rounded-md border border-yellow-700 bg-yellow-900 bg-opacity-10 text-yellow-200 p-3 mb-3 text-sm">
                <i class="fas fa-exclamation-triangle mr-1"></i> 
                ไอเทมประเภทอุปกรณ์ (เช่น หมวก, เสื้อ, อาวุธ) ต้องมีระดับ 8 ดาวขึ้นไป
              </div>

              <!-- เพิ่มข้อความแจ้งเตือนเรื่องการ์ด -->
              <div class="rounded-md border border-red-700 bg-red-900 bg-opacity-10 text-red-200 p-3 mb-4 text-sm">
                <i class="fas fa-ban mr-1"></i> 
                <strong>อวัยวะจักรกลที่มีการ์ดติดตั้งอยู่ ไม่สามารถตั้งขายได้</strong>
              </div>

              <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                <div
                  v-for="it in itemsForPicker"
                  :key="it.id"
                  class="border border-gray-700 rounded-md bg-gray-900 p-2 cursor-pointer transition-all duration-200 hover:border-indigo-600"
                  :class="it.rarityColors?.borderColor || 'border-gray-700'"
                  @click="addItemToSelection(it)"
                  title="คลิกเพื่อเพิ่ม"
                >
                  <div class="w-full aspect-square bg-gray-800 border border-gray-700 rounded-md overflow-hidden flex items-center justify-center">
                    <img
                  :src="it.img || defaultItemImg"
                      :class="it.rarityColors?.borderColor || 'border-gray-700'"
                      alt=""
                      class="w-full h-full object-cover border rounded"
                      @error="handleImageError"
                    />
                  </div>
                  <div class="mt-2">
                    <div class="text-sm font-semibold truncate" :class="it.rarityColors?.textColor || 'text-gray-200'">
                      {{ it.name }} {{ it.level ? `+${it.level}` : '' }}
                    </div>
                    <div class="text-2xs text-gray-400"><i class="fas fa-tag mr-1"></i>{{ it.type }}</div>
                    <div v-if="it.rarity" class="text-2xs flex items-center gap-1">
                      <i class="fas fa-star" :style="{ color: it.rarity === 7 ? '#FFD700' : '#c7caff' }"></i>
                      {{ it.rarity }} ดาว
                    </div>
                  </div>
                  <div class="mt-2 text-sm text-indigo-200 flex items-center gap-1">
                    <i class="fas fa-plus-circle"></i> เพิ่ม
                  </div>
                </div>

                <div v-if="!itemsForPicker.length" class="col-span-full text-xs text-gray-400 border border-dashed border-gray-700 rounded-md px-2 py-4 text-center">
                  ไม่มีไอเทมที่สามารถตั้งขายได้ในขณะนี้<br />
                  (อาจเพราะไอเทมทั้งหมดเป็นอวัยวะจักรกลที่มีการ์ด หรือเป็นอุปกรณ์ระดับต่ำกว่า 8 ดาว)
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex gap-2 border-t border-gray-700 px-4 py-3 sticky bottom-0 bg-gray-900 bg-opacity-95 backdrop-filter backdrop-blur">
              <button
                class="bg-gray-700 hover:bg-gray-600 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-gray-600"
                @click="showItemSelectionModal = false"
                :disabled="isActionLoading"
              >
                <i class="fas fa-check mr-2 text-xs"></i> เสร็จสิ้น
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Shop Items Modal -->
      <transition name="fade">
        <div v-if="showShopItemsModal && selectedShop" class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 animate-modalFade">
          <div class="w-full max-w-2xl bg-gray-800 text-gray-300 rounded-lg p-0 flex flex-col relative shadow-lg border border-gray-700 animate-scaleIn">
            <!-- Head -->
            <div class="flex justify-between items-center border-b border-gray-700 px-4 py-3 sticky top-0 bg-gray-900 bg-opacity-95 backdrop-filter backdrop-blur">
              <h3 class="text-lg font-semibold text-indigo-200">
                <i class="fas fa-shop mr-2"></i> {{ selectedShop.shopName || 'ร้านค้าไม่มีชื่อ' }}
              </h3>
              <button class="text-gray-400 hover:text-indigo-300 transition-colors duration-200" @click="closeShopItems" :disabled="isActionLoading" aria-label="ปิด">
                <i class="fas fa-times text-lg"></i>
              </button>
            </div>
            <!-- Content -->
            <div class="p-4 overflow-auto max-h-60 scrollbar-custom">
              <p class="text-xs text-gray-400 mb-3 flex items-center gap-2">
                <i class="fas" :class="selectedShop.hideIdentity ? 'fa-user-secret' : 'fa-user'"></i>
                {{ selectedShop.hideIdentity ? 'บุคคลลึกลับ' : selectedShop.characterName || 'ไม่ทราบ' }}
                <span class="w-1 h-1 rounded-full bg-gray-500"></span>
                <i class="far fa-clock"></i> ปิดร้าน: {{ formatExpiresAt(selectedShop.expiresAt) }}
              </p>
              <div class="flex flex-col gap-2">
                <div
                  v-for="item in selectedShop.items"
                  :key="item.id"
                  class="flex items-center justify-between gap-2 px-3 py-2 border rounded-md transition-all duration-200 cursor-pointer"
                  :class="selectedItemId === item.id ? 'border-indigo-600 bg-indigo-900 bg-opacity-20' : 'border-gray-700 bg-gray-900 hover:border-indigo-600'"
                  :style="item.rarityColors ? {
                    borderColor: selectedItemId === item.id ? '#4f46e5' : item.rarityColors.borderColor,
                    backgroundColor: item.rarityColors.backgroundColor
                  } : {}"
                  @click="togglePick(item.id)"
                  role="button"
                  :aria-label="`เลือก ${item.name}`"
                >
                  <div class="flex items-center gap-3 min-w-0">
                    <img
                      :src="item.img || defaultItemImg"
                      alt=""
                      class="w-10 h-10 rounded-md object-cover border border-gray-700 bg-gray-800"
                      :class="item.rarityColors?.borderColor || 'border-gray-700'"
                      @error="handleImageError"
                    />
                    <div class="min-w-0">
                      <div class="flex items-center space-x-1">
                        <div class="text-2xs flex items-center gap-1 px-0.5 border border-gray-700 rounded-lg">
                          <i class="fas fa-star text-yellow-500"></i>
                          {{ item.rarity }}
                        </div>
                        <div class="text-sm font-semibold truncate" :class="item.rarityColors?.textColor || 'text-gray-200'">
                          {{ item.name }} {{ item.level ? `+${item.level}` : '' }}
                        </div>
                      </div>
                      <div class="text-2xs text-gray-400 truncate">{{ item.type }}</div>
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <div class="text-sm font-semibold whitespace-nowrap" :style="item.rarityColors ? { color: item.rarityColors.textColor } : { color: '#c7caff' }">
                      <i class="fas fa-gem mr-1 text-xs text-blue-500"></i> {{ item.price }}
                    </div>
                    <button
                      @click.stop="openDetailModal(item)"
                      class="text-blue-400 hover:text-blue-300 w-8 h-8 flex items-center justify-center rounded-md transition-colors duration-200"
                      :disabled="isActionLoading"
                      title="ดูข้อมูลไอเทม"
                    >
                      <i class="fas fa-info-circle text-sm"></i>
                    </button>
                  </div>
                </div>
                <div v-if="!selectedShop.items || selectedShop.items.length === 0" class="text-xs text-gray-400 border border-dashed border-gray-700 rounded-md px-2 py-2 text-center">
                  ไม่มีไอเทมในร้านค้านี้
                </div>
              </div>
              <div v-if="modalError" :class="[modalError === 'ซื้อไอเทมสำเร็จ' ? 'border-green-700 bg-green-900 text-green-200' : 'border-red-700 bg-red-900 text-red-200']" class="mt-3 rounded-md border bg-opacity-10 p-3 text-sm">
                <i class="fas fa-times-circle mr-1"></i> {{ modalError }}
              </div>
            </div>

            <!-- Actions -->
            <div class="flex gap-2 border-t border-gray-700 px-4 py-3 sticky bottom-0 bg-gray-900 bg-opacity-95 backdrop-filter backdrop-blur">
              <button
                v-if="selectedShop.characterId === user?.value?.uid"
                class="bg-red-600 hover:bg-red-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
                @click="closeShop"
                :disabled="isActionLoading"
              >
                <span v-if="!isActionLoading"><i class="fas fa-times-circle mr-2 text-xs"></i> ปิดร้านค้า</span>
                <span v-else class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              </button>
              <button
                v-else-if="selectedItemId"
                class="bg-indigo-600 hover:bg-indigo-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
                @click="buySelectedItem"
                :disabled="isActionLoading"
              >
                <span v-if="!isActionLoading"><i class="fas fa-shopping-bag mr-2 text-xs"></i> ซื้อ</span>
                <span v-else class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              </button>
              <button
                class="bg-gray-700 hover:bg-gray-600 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-gray-600"
                @click="closeShopItems"
                :disabled="isActionLoading"
              >
                <i class="fas fa-times mr-2 text-xs"></i> ปิด
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Detail Item Modal -->
      <DetailItemModal
        v-if="showDetailModal"
        :item="selectedDetailItem"
        :itemId="selectedDetailItem?.id"
        @close="showDetailModal = false"
      />
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { projectFirestore, projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import { useInventoryStore } from '@/composables/useInventoryStore';
import DetailItemModal from '@/components/DetailItemModal.vue';
import gameItems from '@/data/gameItems.json';
import defaultItemImg from '@/assets/default-item.png';

export default {
  name: 'Marketplace',
  components: {
    DetailItemModal
  },
  setup() {
    const { user } = getUser();
    const playerStore = usePlayerStore();

    // Loading states
    const isFetching = ref(true);
    const isActionLoading = ref(false);

    // UI states
    const error = ref(null);
    const warningMessage = ref(null);

    // Sort and Search
    const sortMode = ref('oldest');
    const searchQuery = ref('');

    // Modals
    const showSetShopModal = ref(false);
    const showItemSelectionModal = ref(false);
    const showShopItemsModal = ref(false);
    const showDetailModal = ref(false);

    // Data
    const selectedItems = ref([]);
    const availableItems = ref([]);
    const activeShops = ref([]);
    const hasActiveShop = ref(false);
    const shopName = ref('');
    const hideIdentity = ref(false);
    const selectedShopId = ref(null);
    const selectedItemId = ref(null);
    const selectedDetailItem = ref(null);
    const activeShopsCount = ref(0);
    const anonymouseImg = 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2Fanonymouse.png?alt=media&token=25259b2d-03f1-49fb-b70c-054cd88d2a86';
    const modalError = ref(null);
    const marketplaceActions = projectFunctions.httpsCallable('gameActions');

    let unsubscribe = null;

    const restrictedTypes = [
      'หมวก', 'แว่นตา', 'เสื้อ', 'เสื้อคลุม', 'เกราะ', 'ปลอกแขน', 'ถุงมือ',
      'กางเกง', 'สนับแข้ง', 'รองเท้า', 'เข็มขัด', 'สร้อยคอ', 'แหวน', 'อาวุธ',
      'อาวุธสำรอง', 'ตะกรุด', 'ผ้ายันต์', 'สายสิญจน์'
    ];

    const hydrateItem = (instanceId, item = {}) => {
      // Try templateId first, then fallback to name for gameItems lookup
      let tpl = gameItems[item.templateId] || {};
      if (!tpl.name && item.name) {
        tpl = gameItems[item.name] || {};
      }
      const states = item.states || {};
      return {
        id: instanceId,
        instanceId,
        templateId: item.templateId || item.name,
        ...tpl,
        ...states,
        ...item, // <-- Event data (price, etc.) must override template defaults
        states,
        // Explicitly preserve event-level price (do not let template price override)
        price: item.price,
        transfer: states.transfer ?? tpl.transfer ?? false,
        rarity: states.rarity ?? tpl.rarity ?? 1,
        rarityColors: states.rarityColors ?? tpl.rarityColors ?? null,
        level: states.level ?? null,
        cardSlots: states.cardSlots || [],
        cardIds: (states.cardSlots || []).map(slot => slot.cardTemplateId || slot).filter(Boolean),
        // Include stats from gameItems.json for display
        stats: tpl.stats || {},
        upgradeStats: states.upgradeStats || {},
        cardStats: states.cardStats || {},
      };
    };

    const canListItem = (item) => {
      if (!item.transfer) return false;
      if (restrictedTypes.includes(item.type) && (item.rarity || 1) < 8) return false;
      if (item.type === 'อวัยวะจักรกล' && ((item.cardSlots?.length || 0) > 0 || (item.cardIds?.length || 0) > 0)) return false;
      return true;
    };

    // Cooldown
    const lastShopOpenedAt = ref(null);
    const cooldownRemaining = ref(null);
    const canOpenShop = computed(() => cooldownRemaining.value <= 0);
    const cooldownMessage = computed(() => {
      if (cooldownRemaining.value === null) return '';
      if (cooldownRemaining.value <= 0) return 'ขณะนี้คุณสามารถเปิดร้านได้';
      const hours = Math.floor(cooldownRemaining.value / 3600000);
      const minutes = Math.floor((cooldownRemaining.value % 3600000) / 60000);
      const seconds = Math.floor((cooldownRemaining.value % 60000) / 1000);
      return `สามารถเปิดร้านได้ในอีก ${hours} ชม. ${minutes} นาที ${seconds} วินาที`;
    });
    let countdownInterval = null;

    const selectedShop = computed(() => activeShops.value.find(shop => shop.id === selectedShopId.value));
    const allItemsPriced = computed(() => selectedItems.value.every(item => item.price && item.price >= 10 && item.price <= 100000));

    const formatExpiresAt = (timestamp) => {
      if (!timestamp) return 'ไม่ระบุ';
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString('th-TH', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const calculateNetPrice = (price) => {
      if (!price || price < 10 || price > 100000) return 0;
      return Math.floor(price * (1 - 0.35));
    };

    const loadServerSettings = async () => {
      const settingRef = projectFirestore.collection('server').doc('setting');
      const settingDoc = await settingRef.get();
      if (settingDoc.exists) {
        activeShopsCount.value = settingDoc.data().activeShops || 0;
      }
    };

    const attachRealtime = () => {
      if (unsubscribe) { unsubscribe(); unsubscribe = null; }
      const now = new Date();
      unsubscribe = projectFirestore
        .collection('events')
        .where('type', '==', 'marketplace')
        .where('expiresAt', '>', now)
        .where('finished', '==', false)
        .onSnapshot(async (snap) => {
          try {
            const shops = await Promise.all(
              snap.docs.map(async (doc) => {
                const eventData = doc.data();
                const items = eventData.items.map(it => hydrateItem(it.instanceId || it.id, it));
                let characterName = 'ไม่ทราบ';
                let characterImg = 'default-character.png';
                const charDoc = await projectFirestore.collection('characters').doc(eventData.characterId).get();
                if (charDoc.exists) {
                  const cd = charDoc.data();
                  characterName = cd.characterName || 'ไม่ทราบ';
                  characterImg = cd.imageUrl || 'default-character.png';
                }
                const minPrice = items.length ? Math.min(...items.map(i => Number(i.price) || Infinity)) : null;
                return {
                  id: doc.id,
                  ...eventData,
                  items,
                  itemNames: eventData.itemNames || [],
                  typeNames: eventData.typeNames || [],
                  characterName,
                  characterImg,
                  __minPrice: Number.isFinite(minPrice) ? minPrice : null,
                };
              })
            );
            activeShops.value = shops;

            if (user.value) {
              const charPrivateData = playerStore.characterPrivateData;
              if (charPrivateData && charPrivateData.event) {
                const eventRef = projectFirestore.collection('events').doc(charPrivateData.event);
                const eventDoc = await eventRef.get();
                hasActiveShop.value = !!(eventDoc.exists && eventDoc.data().type === 'marketplace');
              } else {
                hasActiveShop.value = false;
              }
            }

            await loadServerSettings();
            isFetching.value = false;
          } catch (e) {
            error.value = e.message || 'ซิงก์ข้อมูลไม่สำเร็จ';
            isFetching.value = false;
          }
        }, (e) => {
          error.value = e.message || 'เชื่อมต่อเรียลไทม์ไม่สำเร็จ';
          isFetching.value = false;
        });
    };

    const inventoryStore = useInventoryStore();

    const loadInventory = () => {
      if (!user.value) return;
      const bagData = inventoryStore.bagData;
      if (!bagData || !bagData.items) {
        availableItems.value = [];
        return;
      }

      const bagItems = bagData.items || {};
      if (Array.isArray(bagItems) || Object.keys(bagItems).length === 0) {
        availableItems.value = [];
        return;
      }

      availableItems.value = Object.entries(bagItems)
        .map(([instanceId, item]) => hydrateItem(instanceId, item))
        .filter(canListItem);
    };

    const loadCooldown = async () => {
      if (!user.value) return;
      const data = playerStore.characterPrivateData;
      if (data) {
        lastShopOpenedAt.value = data.lastShopOpenedAt || null;
        if (lastShopOpenedAt.value) {
          const last = lastShopOpenedAt.value.toMillis();
          const now = Date.now();
          const cooldownMillis = 48 * 60 * 60 * 1000;
          let remaining = cooldownMillis - (now - last);
          if (remaining < 0) remaining = 0;
          cooldownRemaining.value = remaining;
        } else {
          cooldownRemaining.value = 0;
        }
      }
    };

    const startCountdown = () => {
      if (countdownInterval) clearInterval(countdownInterval);
      if (cooldownRemaining.value > 0) {
        countdownInterval = setInterval(() => {
          cooldownRemaining.value -= 1000;
          if (cooldownRemaining.value <= 0) {
            cooldownRemaining.value = 0;
            clearInterval(countdownInterval);
          }
        }, 1000);
      }
    };

    const openSetShopModal = async () => {
      modalError.value = null;
      showSetShopModal.value = true;
      await loadInventory();
      await loadCooldown();
      startCountdown();
    };

    const closeSetShop = () => {
      if (isActionLoading.value) return;
      showSetShopModal.value = false;
      selectedItems.value = [];
      showDetailModal.value = false;
      shopName.value = '';
      hideIdentity.value = false;
      modalError.value = null;
      if (countdownInterval) clearInterval(countdownInterval);
    };

    const openItemPicker = async () => {
      modalError.value = null;
      await loadInventory();
      showItemSelectionModal.value = true;
    };

    const itemsForPicker = computed(() => {
      const pickedIds = new Set(selectedItems.value.map(i => i.id));
      return availableItems.value
        .filter(item => !pickedIds.has(item.id) && canListItem(item))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    });

    const addItemToSelection = (item) => {
      if (selectedItems.value.length >= 10) {
        modalError.value = 'คุณเลือกไอเทมครบ 10 ชิ้นแล้ว';
        return;
      }
      selectedItems.value.push({ ...item, price: 10 });
    };

    const removeItem = (index) => {
      selectedItems.value.splice(index, 1);
      modalError.value = null;
    };

    const openShopItemsModal = (shop) => {
      selectedShopId.value = shop.id;
      selectedItemId.value = null;
      modalError.value = null;
      showShopItemsModal.value = true;
    };

    const closeShopItems = () => {
      if (isActionLoading.value) return;
      showShopItemsModal.value = false;
      showDetailModal.value = false;
      selectedShopId.value = null;
      selectedItemId.value = null;
      selectedDetailItem.value = null;
      modalError.value = null;
    };

    const openDetailModal = (item) => {
      selectedDetailItem.value = item;
      showDetailModal.value = true;
    };

    const handleImageError = (e) => { e.target.src = defaultItemImg; };

    const togglePick = (id) => { selectedItemId.value = selectedItemId.value === id ? null : id; };

    const buySelectedItem = async () => {
      if (!user.value) { modalError.value = 'กรุณาล็อกอินเพื่อซื้อไอเทม'; return; }
      if (!selectedShop.value || !selectedItemId.value) return;
      isActionLoading.value = true;
      modalError.value = null;
      try {
        await marketplaceActions({
          action: 'marketplaceAction',
          subAction: 'buyItem',
          eventId: selectedShop.value.id,
          instanceId: selectedItemId.value
        });
        selectedItemId.value = null;
        modalError.value = 'ซื้อไอเทมสำเร็จ';
      } catch (err) {
        modalError.value = err?.message || 'ซื้อไม่สำเร็จ';
      } finally {
        isActionLoading.value = false;
      }
    };

    const confirmSetShop = async () => {
      if (!user.value) { modalError.value = 'กรุณาล็อกอินก่อน'; return; }
      if (!shopName.value) { modalError.value = 'กรุณาตั้งชื่อร้านค้า'; return; }
      if (shopName.value.length > 50) { modalError.value = 'ชื่อร้านค้าต้องไม่เกิน 50 อักษร'; return; }
      if (selectedItems.value.length === 0) { modalError.value = 'กรุณาเลือกอย่างน้อย 1 ไอเทม'; return; }
      if (!allItemsPriced.value) { modalError.value = 'กรุณาตั้งราคาขั้นต่ำ 10 และสูงสุด 100,000 เพชรสำหรับทุกไอเทม'; return; }
      isActionLoading.value = true;
      modalError.value = null;
      try {
        const itemsData = selectedItems.value.map(i => ({ instanceId: i.instanceId || i.id, price: i.price }));
        await marketplaceActions({
          action: 'marketplaceAction',
          subAction: 'setMarketplace',
          items: itemsData,
          shopName: shopName.value,
          hideIdentity: hideIdentity.value,
        });
        showSetShopModal.value = false;
        selectedItems.value = [];
        shopName.value = '';
        hideIdentity.value = false;
        modalError.value = null;
      } catch (err) {
        modalError.value = err?.message || 'ตั้งร้านค้าไม่สำเร็จ';
      } finally {
        isActionLoading.value = false;
      }
    };

    const closeShop = async () => {
      if (!user.value) { modalError.value = 'กรุณาล็อกอินก่อน'; return; }
      if (!selectedShop.value) return;
      isActionLoading.value = true;
      modalError.value = null;
      try {
        await marketplaceActions({
          action: 'marketplaceAction',
          subAction: 'closeShop',
          eventId: selectedShop.value.id
        });
        showShopItemsModal.value = false;
        selectedShopId.value = null;
        selectedItemId.value = null;
        modalError.value = 'ปิดร้านค้าสำเร็จ';
      } catch (err) {
        modalError.value = err?.message || 'ปิดร้านค้าไม่สำเร็จ';
      } finally {
        isActionLoading.value = false;
      }
    };

    const displayedShops = computed(() => {
      let list = [...activeShops.value];
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase().trim();
        list = list.filter(shop =>
          shop.itemNames.some(name => name.toLowerCase().includes(query)) ||
          shop.typeNames.some(type => type.toLowerCase().includes(query))
        );
      }
      if (sortMode.value === 'oldest') {
        return list.sort((a, b) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0));
      }
      return list.sort((b, a) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0));
    });

    onMounted(async () => {
      if (!user.value) {
        error.value = 'กรุณาล็อกอินเพื่อเข้าสู่ตลาด';
        isFetching.value = false;
        return;
      }
      playerStore.init(user.value.uid);
      await loadInventory();
      await loadServerSettings();
      attachRealtime();
    });

    onBeforeUnmount(() => {
      if (unsubscribe) unsubscribe();
      if (countdownInterval) clearInterval(countdownInterval);
    });

    return {
      isFetching,
      isActionLoading,
      error,
      warningMessage,
      sortMode,
      searchQuery,
      showSetShopModal,
      showItemSelectionModal,
      showShopItemsModal,
      showDetailModal,
      selectedItems,
      availableItems,
      activeShops,
      hasActiveShop,
      shopName,
      hideIdentity,
      selectedShop,
      selectedItemId,
      selectedDetailItem,
      activeShopsCount,
      anonymouseImg,
      defaultItemImg,
      modalError,
      canOpenShop,
      cooldownMessage,
      allItemsPriced,
      displayedShops,
      itemsForPicker,
      openSetShopModal,
      closeSetShop,
      openItemPicker,
      openShopItemsModal,
      closeShopItems,
      openDetailModal,
      addItemToSelection,
      removeItem,
      handleImageError,
      togglePick,
      buySelectedItem,
      confirmSetShop,
      closeShop,
      formatExpiresAt,
      calculateNetPrice,
    };
  },
};
</script>

<style scoped>
/* Background */
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fmarketplace.png?alt=media&token=eb37d080-b738-46c1-9a96-0eb7002bd2e5') no-repeat center center fixed,
              linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
  background-size: cover;
}

/* Animations */
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.3s ease-out; }
@keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.3s ease-out; }

/* Scrollbar */
.scrollbar-custom::-webkit-scrollbar { width: 6px; }
.scrollbar-custom::-webkit-scrollbar-track { background: #374151; border-radius: 10px; }
.scrollbar-custom::-webkit-scrollbar-thumb { background: #4b5563; border-radius: 10px; }
.scrollbar-custom::-webkit-scrollbar-thumb:hover { background: #6b7280; }

/* Chips */
.chip {
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  line-height: 1rem;
  border: 1px solid #374151;
  background: #111827;
  color: #c7caff;
}
.chip-green {
  background: rgba(6, 78, 59, 0.3);
  color: #bbf7d0;
  border-color: #065f46;
}
.chip-amber {
  background: rgba(120, 53, 15, 0.2);
  color: #fde68a;
  border-color: #92400e;
}
.chip-muted {
  background: #111827;
  color: #9ca3af;
  border-color: #374151;
}

/* Text-2xs */
.text-2xs { font-size: 0.7rem; line-height: 0.95rem; }

/* Transitions */
.fade-enter-active, .fade-leave-active { transition: opacity .18s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
